import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminLessonService, adminCursoService } from '../../shared/services';
import { Save, X, ArrowLeft, PlayCircle } from 'lucide-react';

export function InstructorLessonFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    courseId: '',
    title: '',
    description: '',
    order: 1,
    videoId: '',
    videoType: 'Upload' as 'Upload' | 'YouTube',
    youtubeUrl: '',
  });

  // Buscar cursos para o select
  const { data: cursosData } = useQuery({
    queryKey: ['admin-cursos-list'],
    queryFn: () => adminCursoService.getCursos({}),
  });

  // Buscar lesson se estiver editando
  const { data: lesson } = useQuery({
    queryKey: ['admin-lesson', id],
    queryFn: () => adminLessonService.getLesson(id!),
    enabled: isEditing,
  });

  useEffect(() => {
    if (lesson) {
      setFormData({
        courseId: lesson.courseId,
        title: lesson.title,
        description: lesson.description || '',
        order: lesson.order,
        videoId: lesson.videoId || '',
        videoType: lesson.videoType,
        youtubeUrl: lesson.youtubeUrl || '',
      });
    }
  }, [lesson]);

  const createMutation = useMutation({
    mutationFn: (data: any) => {
      if (data.videoType === 'YouTube') {
        return adminLessonService.createYouTubeLesson({
          title: data.title,
          description: data.description,
          order: data.order,
          courseId: data.courseId,
          youTubeUrl: data.youtubeUrl,
        });
      }
      return adminLessonService.createLesson(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-lessons'] });
      navigate('/instructor/lessons');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: typeof formData) => adminLessonService.updateLesson(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-lessons'] });
      queryClient.invalidateQueries({ queryKey: ['admin-lesson', id] });
      navigate('/instructor/lessons');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.courseId) {
      alert('Selecione um curso para a aula');
      return;
    }

    if (!formData.title.trim()) {
      alert('O título da aula é obrigatório');
      return;
    }
    
    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const getCursoNome = (courseId: string) => {
    const curso = (cursosData as any[])?.find((c: any) => c.id === courseId);
    return curso?.titulo || curso?.title || '';
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/instructor/lessons"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft size={20} />
          Voltar para aulas
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditing ? 'Editar Aula' : 'Nova Aula'}
        </h1>
        <p className="text-gray-600 mt-2">
          {isEditing 
            ? 'Atualize as informações da aula'
            : 'Adicione uma nova aula ao curso'
          }
        </p>
      </div>

      {/* Formulário */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="space-y-6">
          {/* Curso */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Curso <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.courseId}
              onChange={(e) => setFormData({ ...formData, courseId: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              disabled={isEditing} // Não permite mudar o curso ao editar
            >
              <option value="">Selecione um curso</option>
              {(cursosData as any[])?.map((curso: any) => (
                <option key={curso.id} value={curso.id}>
                  {curso.titulo || curso.title}
                </option>
              ))}
            </select>
            {formData.courseId && (
              <p className="text-sm text-gray-500 mt-1">
                Curso selecionado: {getCursoNome(formData.courseId)}
              </p>
            )}
          </div>

          {/* Título */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Título da Aula <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ex: Introdução ao Excel"
              required
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Descreva o conteúdo da aula..."
            />
          </div>

          {/* Ordem e Tipo de Vídeo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ordem <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 1 })}
                min={1}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Posição da aula no curso
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Vídeo <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.videoType}
                onChange={(e) => setFormData({ ...formData, videoType: e.target.value as 'Upload' | 'YouTube' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Upload">Vídeo Upload (Hospedado)</option>
                <option value="YouTube">Vídeo YouTube</option>
              </select>
              <p className="text-sm text-gray-500 mt-1">
                Escolha a origem do vídeo
              </p>
            </div>
          </div>

          {/* Campos condicionais por tipo */}
          {formData.videoType === 'Upload' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Video ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.videoId}
                onChange={(e) => setFormData({ ...formData, videoId: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="ID do vídeo no sistema"
                required={formData.videoType === 'Upload'}
              />
              <p className="text-sm text-gray-500 mt-1">
                Identificador único do vídeo já upado
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL do YouTube <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={formData.youtubeUrl}
                onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://www.youtube.com/watch?v=..."
                pattern="https://(www\.)?youtube\.com/.*|https://youtu\.be/.*"
                required={formData.videoType === 'YouTube'}
              />
              <p className="text-sm text-gray-500 mt-1">
                Cole a URL completa do vídeo do YouTube
              </p>
            </div>
          )}
        </div>

        {/* Botões */}
        <div className="flex items-center gap-4 mt-8 pt-6 border-t border-gray-200">
          <button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={20} />
            {createMutation.isPending || updateMutation.isPending 
              ? 'Salvando...' 
              : isEditing ? 'Atualizar Aula' : 'Criar Aula'
            }
          </button>

          <Link
            to="/instructor/lessons"
            className="flex items-center gap-2 px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <X size={20} />
            Cancelar
          </Link>
        </div>

        {/* Preview */}
        {formData.title && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Preview</h3>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex items-start gap-3">
                <PlayCircle className="text-blue-600 flex-shrink-0 mt-1" size={24} />
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">
                    {formData.order}. {formData.title}
                  </h4>
                  {formData.description && (
                    <p className="text-sm text-gray-600 mt-1">
                      {formData.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                      {formData.videoType === 'YouTube' ? '▶ YouTube' : '📹 Upload'}
                    </span>
                    {formData.courseId && (
                      <span className="text-xs text-gray-500">
                        {getCursoNome(formData.courseId)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
